import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { contentRect, toShoppable } from '../shoppable-video/shoppable-video';
import type { ShoppableProduct } from '../silvr/types';
import { PublishedClip, trackPositionAt } from '../../utils/clipApi';
import ShopPanel, { type SheetFocus } from './ShopPanel';
import TvControls from './TvControls';
import SilvrMark from './SilvrMark';
import '../silvr/silvr.scss';
import '../shoppable-video/shoppable-video.scss';
import './tv.scss';

interface TvShoppableVideoProps {
  clip: PublishedClip;
  /** The `source` on every tracked listing link: one value per demo (e.g. "demo-pll"). */
  clickSource: string;
  /** Over the picture, bottom left, like a streaming player: the show, then "S2 E7". */
  title: string;
  episode: string;
}

const productKey = (product: ShoppableProduct) => `${product.brand}|${product.name}|${product.image}`;

/** One card per product: two garments that resolve to the same product are one item to buy. */
const uniqueByProduct = (products: ShoppableProduct[]) => {
  const seen = new Set<string>();
  return products.filter((product) => {
    const key = productKey(product);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** The line from a card to its garment, in the player's own coordinates. */
interface Pointer {
  id: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
}

/**
 * The "Shop by TV" player (Paula's mockups, 2026-10-24; reviewed by Jason and Josh, 2026-10-27).
 * Paused, a gold "Shop this scene" chip with the Silvr mark sits top right. The chip marks every
 * published garment in the frame with a static white circle and opens the dark side sheet with those
 * garments. The circles only show where things are: they are not buttons, since a "+" read as an action
 * to take. On the scene list the card under the pointer (the first one otherwise) draws a quick line to
 * its circle and frames it; an item open in the sheet keeps its circle framed (SheetFocus). Closing the sheet clears the picture back to the paused frame; a click resumes it.
 */
const TvShoppableVideo: React.FC<TvShoppableVideoProps> = ({ clip, clickSource, title, episode }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(true);
  const [started, setStarted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [frame, setFrame] = useState(0);
  const [overlay, setOverlay] = useState({ left: 0, top: 0, width: 0, height: 0 });
  const [panelOpen, setPanelOpen] = useState(false);
  const [focus, setFocus] = useState<SheetFocus>({ kind: 'scene', hoverId: null });
  const [pointer, setPointer] = useState<Pointer | null>(null);
  // Counts every time the viewer points at a scene card, so the line draws again each time, even on the
  // card it already joined after it has faded (Jason, 2026-10-29).
  const [pointCount, setPointCount] = useState(0);
  // True once the sheet has finished sliding in (0.3 s): until then its cards are still moving, and a
  // line measured from them would start at the edge of the player and jump into place.
  const [sheetSettled, setSheetSettled] = useState(false);

  const fps = clip.source.fps || 24;

  // --- what is on screen right now, and in the whole clip
  const products = useMemo(() => {
    const out: ShoppableProduct[] = [];
    for (const track of clip.tracks) {
      const position = trackPositionAt(track, frame);
      if (position) out.push(toShoppable(track, position, clickSource));
    }
    return out;
  }, [clip, frame, clickSource]);

  const sceneItems = useMemo(() => uniqueByProduct(products), [products]);

  const episodeItems = useMemo(
    () => uniqueByProduct(clip.tracks.map((track) => toShoppable(track, { x: 0, y: 0 }, clickSource))),
    [clip, clickSource],
  );

  // The garment the sheet is about (Jason, 2026-10-29): on the scene list, the card under the pointer
  // or the first one, highlighted and joined by a line; an item open in the sheet (its Details,
  // its Similar matches, or a match's Details), framed with no line; "Shop this episode", nothing.
  const activeKey = useMemo(() => {
    if (!panelOpen || focus.kind === 'none') return null;
    if (focus.kind === 'open') return productKey(focus.item);
    const active = sceneItems.find((item) => item.id === focus.hoverId) ?? sceneItems[0];
    return active ? productKey(active) : null;
  }, [panelOpen, focus, sceneItems]);
  // The line starts at a card, so only the scene list draws it.
  const activeId = panelOpen && focus.kind === 'scene' ? focus.hoverId ?? sceneItems[0]?.id ?? null : null;

  // --- video plumbing
  const syncFrame = useCallback(() => {
    const video = videoRef.current;
    if (video) setFrame(Math.round(video.currentTime * fps));
  }, [fps]);

  const syncOverlay = useCallback(() => {
    const video = videoRef.current;
    if (video) setOverlay(contentRect(video));
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const resize = new ResizeObserver(syncOverlay);
    resize.observe(video);
    video.addEventListener('loadedmetadata', syncOverlay);
    video.addEventListener('timeupdate', syncFrame);
    video.addEventListener('seeked', syncFrame);
    return () => {
      resize.disconnect();
      video.removeEventListener('loadedmetadata', syncOverlay);
      video.removeEventListener('timeupdate', syncFrame);
      video.removeEventListener('seeked', syncFrame);
    };
  }, [syncFrame, syncOverlay]);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // --- the line from the active card to its circle, measured on the page and kept in step with the
  // sheet's scroll, the player's size and the sheet sliding in.
  const measurePointer = useCallback(() => {
    const player = playerRef.current;
    if (!player || !activeId || !sheetSettled) {
      setPointer(null);
      return;
    }
    const card = player.querySelector<HTMLElement>(`.tv-card[data-item-id="${activeId}"]`);
    const dot = player.querySelector<HTMLElement>(`.tv-dot[data-item-id="${activeId}"]`);
    const scroller = player.querySelector<HTMLElement>('[data-tv-scroll]');
    const sheet = player.querySelector<HTMLElement>('.tv-panel');
    if (!card || !dot || !scroller || !sheet) {
      setPointer(null);
      return;
    }
    const base = player.getBoundingClientRect();
    const c = card.getBoundingClientRect();
    const d = dot.getBoundingClientRect();
    const s = scroller.getBoundingClientRect();
    // No line to a circle the sheet covers (a garment on the right of the picture, or any garment on a
    // phone, where the sheet comes up from the bottom): it would run across the cards to nothing
    // (Jason, 2026-10-30). The card keeps its frame.
    const sh = sheet.getBoundingClientRect();
    const dx = d.left + d.width / 2;
    const dy = d.top + d.height / 2;
    if (dx >= sh.left && dx <= sh.right && dy >= sh.top && dy <= sh.bottom) {
      setPointer(null);
      return;
    }
    // From the card's left edge, at its middle, kept inside the visible part of the sheet.
    const y = Math.min(Math.max(c.top + c.height / 2, s.top + 12), s.bottom - 12);
    setPointer({
      id: activeId,
      from: { x: c.left - base.left, y: y - base.top },
      to: { x: d.left + d.width / 2 - base.left, y: d.top + d.height / 2 - base.top },
    });
  }, [activeId, sheetSettled]);

  useEffect(() => {
    if (!panelOpen) {
      setSheetSettled(false);
      return undefined;
    }
    const settle = window.setTimeout(() => setSheetSettled(true), 320);
    return () => window.clearTimeout(settle);
  }, [panelOpen]);

  useLayoutEffect(() => {
    if (!panelOpen) {
      setPointer(null);
      return undefined;
    }
    measurePointer();
    const player = playerRef.current;
    const scroller = player?.querySelector('[data-tv-scroll]');
    scroller?.addEventListener('scroll', measurePointer, { passive: true });
    window.addEventListener('resize', measurePointer);
    return () => {
      scroller?.removeEventListener('scroll', measurePointer);
      window.removeEventListener('resize', measurePointer);
    };
  }, [panelOpen, measurePointer, overlay, frame]);

  const handlePlay = () => {
    setStarted(true);
    setIsPaused(false);
    setPanelOpen(false);
    setFocus({ kind: 'scene', hoverId: null });
  };

  const handlePause = () => {
    if (document.pictureInPictureElement) return;
    syncFrame();
    setIsPaused(true);
  };

  const togglePlayback = (event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('.video-controls-bar, [data-silvr-ui], .silvr-ui')) return;
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
  };

  /** The chip: the circles on the garments, and the sheet with all of them. */
  const shopScene = () => {
    videoRef.current?.pause();
    setFocus({ kind: 'scene', hoverId: null });
    setPanelOpen(true);
  };

  /** Closing the sheet takes the circles off with it: the paused frame is a picture again, and a click
   *  plays it (2026-10-27 review). The chip stays, to come back. */
  const changeFocus = (next: SheetFocus) => {
    setFocus(next);
    if (next.kind === 'scene' && next.hoverId) setPointCount((count) => count + 1);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setFocus({ kind: 'scene', hoverId: null });
  };

  const sceneMode = started && isPaused;
  // The show and episode only, no timestamp (Paula, 2026-10-29).
  const contextLabel = `${title} · ${episode}`;

  return (
    <div ref={playerRef} className={`shoppable-video tv-player ${isFullscreen ? 'is-fullscreen' : ''} ${panelOpen ? 'has-panel' : ''}`} onClick={togglePlayback}>
      <video
        id="player"
        ref={videoRef}
        src={clip.video_url}
        playsInline
        preload="metadata"
        onLoadedMetadata={syncFrame}
        onPlay={handlePlay}
        onPause={handlePause}
        onSeeked={syncFrame}
        onEnded={handlePause}
        className={sceneMode && panelOpen ? 'is-shopping' : ''}
      />
      {/* Laid out inside the video's own pixel rectangle, so a circle at (x%, y%) lands on the garment.
          Markers only: aria-hidden and not clickable, the sheet lists the same garments. */}
      <div className="shoppable-video__frame tv-dots" style={overlay} aria-hidden="true">
        {sceneMode &&
          panelOpen &&
          products.map((product, index) => (
            <span
              key={product.id}
              className={`tv-dot ${activeKey && productKey(product) === activeKey ? 'is-active' : ''}`}
              data-item-id={product.id}
              style={{ left: `${product.x}%`, top: `${product.y}%`, animationDelay: `${0.05 + index * 0.07}s` }}
            />
          ))}
      </div>

      {pointer && (
        <svg className="tv-pointer" aria-hidden="true">
          {/* Keyed by the card and the pointing, so the line draws itself again whenever a card is pointed at. */}
          <line key={`${pointer.id}:${pointCount}`} className="tv-pointer__line" pathLength={1} x1={pointer.from.x} y1={pointer.from.y} x2={pointer.to.x} y2={pointer.to.y} />
        </svg>
      )}

      {sceneMode && !panelOpen && (
        <button type="button" className="tv-chip silvr-ui" data-silvr-ui="true" onClick={(event) => { event.stopPropagation(); shopScene(); }}>
          <SilvrMark size={14} /> Shop this scene
        </button>
      )}

      {(!started || isPaused) && (
        <div className="tv-title" aria-hidden={panelOpen}>
          <p className="tv-title__show">{title}</p>
          <p className="tv-title__episode">{episode}</p>
        </div>
      )}

      {/* HBO Max's playback controls (TvControls); the keys go to the side sheet while it is open. */}
      <TvControls videoRef={videoRef} playerRef={playerRef} isPaused={isPaused} keysEnabled={!panelOpen} />

      <ShopPanel
        open={panelOpen}
        onClose={closePanel}
        sceneItems={sceneItems}
        episodeItems={episodeItems}
        contextLabel={contextLabel}
        onFocusChange={changeFocus}
        pointedId={activeId}
      />
    </div>
  );
};

export default TvShoppableVideo;
